import * as React from "react";
import { Button } from "@/components/ui/button";
import { FloatingOrbs } from "./FloatingOrbs";

type Props = {
  eyebrow?: string;
  title: string;
  titleAccent?: string;
  subtitle?: string;
  primaryHref?: string;
  primaryLabel?: string;
  secondaryHref?: string;
  secondaryLabel?: string;
  compact?: boolean;
  children?: React.ReactNode;
  rightContent?: React.ReactNode;
  variant?: "dark" | "light";
};

export function HeroBanner({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  primaryHref = "/shop",
  primaryLabel = "Shop Tires",
  secondaryHref = "tel:2898516911",
  secondaryLabel = "Call (289) 851-6911",
  compact = false,
  children,
  rightContent,
  variant = "dark",
}: Props) {
  const isAsymmetric = Boolean(rightContent);
  const isLight = variant === "light";

  const renderTitle = () => {
    if (!titleAccent) {
      return (
        <h1
          className={`text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl ${
            isLight ? "text-foreground" : "text-white"
          } ${isAsymmetric ? "lg:text-left" : "text-center"}`}
        >
          {title}
        </h1>
      );
    }

    const idx = title.indexOf(titleAccent);
    if (idx === -1) {
      return (
        <h1
          className={`text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl ${
            isLight ? "text-foreground" : "text-white"
          } ${isAsymmetric ? "lg:text-left" : "text-center"}`}
        >
          {title}
        </h1>
      );
    }

    const before = title.slice(0, idx);
    const after = title.slice(idx + titleAccent.length);

    return (
      <h1
        className={`text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl ${
          isLight ? "text-foreground" : "text-white"
        } ${isAsymmetric ? "lg:text-left" : "text-center"}`}
      >
        {before}
        <span className="gradient-text">{titleAccent}</span>
        {after}
      </h1>
    );
  };

  return (
    <section
      role="banner"
      className={`relative isolate overflow-hidden ${isLight ? "bg-background" : "text-white"}`}
    >
      {/* Background */}
      {!isLight && (
        <div aria-hidden className="absolute inset-0 -z-10">
          <img
            src="/images/Hero.png"
            alt="APA Automotive full-service garage in Milton, Ontario"
            className="h-full w-full object-cover"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#040C1A]/90 via-[#040C1A]/70 to-[#040C1A]/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#040C1A]/30 via-transparent to-[#040C1A]/60" />
        </div>
      )}

      {/* Floating orbs */}
      <FloatingOrbs variant={isLight ? "mixed" : "blue"} className="-z-[5]" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={compact ? "py-10 sm:py-14 lg:py-16" : "py-14 sm:py-20 lg:py-24"}>
          {isAsymmetric ? (
            <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-center">
              {/* Left: text content */}
              <div>
                {eyebrow && (
                  <p
                    className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                      isLight ? "text-primary" : "text-[#00AADD]"
                    }`}
                  >
                    {eyebrow}
                  </p>
                )}

                <div className="mt-4">{renderTitle()}</div>

                {subtitle && (
                  <p
                    className={`mt-5 max-w-lg text-lg leading-relaxed lg:text-left ${
                      isLight ? "text-muted-foreground" : "text-white/60"
                    }`}
                  >
                    {subtitle}
                  </p>
                )}

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  {primaryHref && (
                    <Button
                      asChild
                      variant="automotive"
                      size="lg"
                      className="rounded-full px-10 shadow-[0_0_30px_rgba(0,170,221,0.4)] hover:shadow-[0_0_40px_rgba(0,170,221,0.6)] transition-shadow duration-300"
                    >
                      <a href={primaryHref}>{primaryLabel}</a>
                    </Button>
                  )}
                  {secondaryHref && (
                    <a
                      href={secondaryHref}
                      className={`inline-flex h-11 items-center justify-center rounded-full border px-7 text-sm font-medium transition-all ${
                        isLight
                          ? "border-border bg-card text-foreground hover:bg-muted"
                          : "border-white/20 bg-white/[0.06] text-white/80 backdrop-blur-sm hover:bg-white/[0.12] hover:border-white/30"
                      }`}
                    >
                      {secondaryLabel}
                    </a>
                  )}
                </div>
              </div>

              {/* Right: custom content */}
              <div className="flex justify-center lg:justify-end">{rightContent}</div>
            </div>
          ) : (
            /* Centered layout */
            <div className="text-center">
              {eyebrow && (
                <p
                  className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                    isLight ? "text-primary" : "text-[#00AADD]"
                  }`}
                >
                  {eyebrow}
                </p>
              )}

              <div className="mt-4 mx-auto max-w-4xl">{renderTitle()}</div>

              {subtitle && (
                <p
                  className={`mt-5 mx-auto max-w-2xl text-lg leading-relaxed ${
                    isLight ? "text-muted-foreground" : "text-white/60"
                  }`}
                >
                  {subtitle}
                </p>
              )}

              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                {primaryHref && (
                  <Button
                    asChild
                    variant="automotive"
                    size="lg"
                    className="w-full rounded-full px-10 sm:w-auto shadow-[0_0_30px_rgba(0,170,221,0.45)] hover:shadow-[0_0_40px_rgba(0,170,221,0.65)] transition-shadow duration-300"
                  >
                    <a href={primaryHref}>{primaryLabel}</a>
                  </Button>
                )}
                {secondaryHref && (
                  <a
                    href={secondaryHref}
                    className={`inline-flex h-11 items-center justify-center rounded-full border px-7 text-sm font-medium transition-all w-full sm:w-auto ${
                      isLight
                        ? "border-border bg-card text-foreground hover:bg-muted"
                        : "border-white/20 bg-white/[0.06] text-white/80 backdrop-blur-sm hover:bg-white/[0.12] hover:border-white/30"
                    }`}
                  >
                    {secondaryLabel}
                  </a>
                )}
              </div>

              {children && <div className="mt-10">{children}</div>}
            </div>
          )}
        </div>
      </div>

      {!isLight && <div aria-hidden className="section-divider absolute inset-x-0 bottom-0" />}
    </section>
  );
}
